import React, { type ReactElement } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { mapValues } from 'lodash';
import {
  DelimitedNumericArrayParam,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault
} from 'use-query-params';
import Card from 'atom-components/card';
import TablePagination from 'atom-components/table-pagination';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import {
  defaultPage,
  defaultPaginationFilters,
  generatePaginationFilters
} from 'helpers/pagination.helpers';
import { toArrayOfNumbers } from 'helpers/query-params.helpers';
import {
  defaultSortingFilter,
  fromSortingState,
  isColumnSorted,
  sortDirection,
  toSortingState
} from 'helpers/sorting.helpers';
import MarkingPageLayout from 'pages/marking/layouts/marking-page-layout';
import type { FilterFormValues } from 'pages/marking/subpages/marking-groups-tab/filter-form/filter-form.schema';
import { useGroupsWithNoDefaults } from 'services/assist-api/entities/group/groups';
import type { GroupWithGroupUsers } from 'services/assist-api/entities/group/types';
import { GroupType } from 'services/assist-api/entities/group/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';
import FilterForm from './filter-form';
import MarkingGroupsTable from './marking-groups-table';

const defaultTableFilters = {
  groupId: withDefault(StringParam, ''),
  groupName: withDefault(StringParam, ''),
  markerOrganisationUserIds: withDefault(DelimitedNumericArrayParam, [])
};

export default function MarkingGroupsPage(): ReactElement {
  const [tableFilters, setTableFilters] = useQueryParams(defaultTableFilters);
  const [sortingFilter, setSortingFilter] = useQueryParam('sorting', defaultSortingFilter);
  const [paginationFilters, setPaginationFilters] = useQueryParams(defaultPaginationFilters);

  // Get the current user's organisation from the cache. We should have an earlier check to ensure
  // that the user has joined and is switched to an organisation by this point:
  const currentOrganisation = useAuthCustomer();

  function generateMarkingGroupFilters(): EntityFilters {
    const filters: EntityFilters = {};

    // Start table row filters.
    if (tableFilters.groupId) {
      filters.id = tableFilters.groupId;
    }
    if (tableFilters.groupName) {
      filters.name = tableFilters.groupName;
    }
    if (tableFilters.markerOrganisationUserIds.length > 0) {
      filters['users.user'] = toArrayOfNumbers(tableFilters.markerOrganisationUserIds);
    }
    // End table row filters.

    // Start column sorting filters.
    if (isColumnSorted('groupId', sortingFilter)) {
      filters['order[id]'] = sortDirection('groupId', sortingFilter);
    }
    if (isColumnSorted('groupName', sortingFilter)) {
      filters['order[name]'] = sortDirection('groupName', sortingFilter);
    }
    if (isColumnSorted('description', sortingFilter)) {
      filters['order[description]'] = sortDirection('description', sortingFilter);
    }
    // End column sorting filters.

    return filters;
  }

  const currentYear = new Date().getFullYear().toString();
  const markingGroupsQuery = useGroupsWithNoDefaults<GroupWithGroupUsers>(
    {
      filters: {
        type: GroupType.MarkingGroup,
        customer: currentOrganisation?.id,
        year: currentYear,
        ...generatePaginationFilters(paginationFilters.pageSize, paginationFilters.page),
        ...generateMarkingGroupFilters()
      },
      groups: ['id', 'group-description', 'group-year', 'group-users', 'groupsusers-user'],
      include: 'users'
    },
    {
      enabled:
        !!(paginationFilters?.pageSize && paginationFilters?.page) && !!currentOrganisation?.id
    }
  );

  const isLoading = isInitialLoad(markingGroupsQuery);
  const { isError } = markingGroupsQuery;

  return (
    <MarkingPageLayout>
      <Card>
        <Card.Header>
          <Card.Heading level={3}>Marking Groups</Card.Heading>
        </Card.Header>
        <Card.Body>
          <FilterForm
            defaultValuesFromURLParams={{
              groupId: tableFilters.groupId,
              groupName: tableFilters.groupName,
              markerOrganisationUserIds: toArrayOfNumbers(tableFilters.markerOrganisationUserIds)
            }}
            onSubmit={(formValues: FilterFormValues): void => {
              setTableFilters({ ...formValues });
              setPaginationFilters({ page: defaultPage });
            }}
            onReset={(): void => {
              setTableFilters(mapValues(defaultTableFilters, () => undefined));
              setPaginationFilters({ page: defaultPage });
            }}
          />
          {!isLoading && isError && <LoadingErrorAlert queries={[markingGroupsQuery]} />}
          <MarkingGroupsTable
            isLoading={isLoading}
            markingGroups={markingGroupsQuery.data?.json || []}
            initialSorting={toSortingState(sortingFilter)}
            onSort={(sorting: SortingState): void => setSortingFilter(fromSortingState(sorting))}
          />
        </Card.Body>
        <Card.Footer>
          <TablePagination
            query={markingGroupsQuery}
            onChange={({ pageSize, page }): void => setPaginationFilters({ pageSize, page })}
          />
        </Card.Footer>
      </Card>
    </MarkingPageLayout>
  );
}
